import React, {useEffect, useState} from "react";
import InfoOutlineIcon from '@rsuite/icons/InfoOutline';
import {IconButton, Input, InputGroup, toaster, Toggle, Tooltip, Whisper} from "rsuite";
import UpdateButton from "../../shared/UpdateButton/UpdateButton";
import {TfiSaveIcon} from "../../../models/Icons.model";
import {ClockStation} from "../../../Targets";
import {ReadImpulse, WriteChannelMode} from "../../../Commands";
import {BitArrayToNumber, NumberToBitArray} from "../../../Converters";
import {
  BlockConfigItem,
  BlocksControlProps,
  BlockState,
  ExecuteCommandResult,
  ImpulseChannel
} from "./BlocksControl.model";
import {BlocksControlUtils} from "./BlocksControl.utils";
import {SharedApi} from "../../../services/Shared.api";
import NotificationFactory from "../../../shared/Notifications";

const BlocksControl = ({ deviceId, executeCommand }: BlocksControlProps) => {
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [blocks, setBlocks] = useState<BlockState>({
    scheduleLock: false,
    astronomicalRelayLock: false,
    timeRelaysLock: false,
    syncLock: false,
    channelLocks: [false, false, false, false],
    newYearChimesLock: false,
    clockChimesLock: false
  });
  const [mode,  setMode] = useState(-1);

  const createChannelPlaceholder = (channel: number): ImpulseChannel => ({
    channel: channel,
    impulse: 0,
    enabled: false,
    sync: false,
    available: true,
    hours: 0,
    minutes: 0,
    amperage: undefined,
    state: 0,
    timeChanged: false
  })

  const [impulseDataList, setImpulseDataList] = useState<ImpulseChannel[]>([
    createChannelPlaceholder(0),
    createChannelPlaceholder(1),
    createChannelPlaceholder(2),
    createChannelPlaceholder(3)
  ]);

  const writeChannelStateCommand = async (newMode: number, newImpulseDataList: ImpulseChannel[]) => {
    setLoading(true);

    try {
      const enabledList = newImpulseDataList.map(x => x.enabled);
      const syncList = newImpulseDataList.map(x => x.sync);
      const unionData = [...enabledList, ...syncList].reverse();

      const flag = BitArrayToNumber(unionData);

      await executeCommand(ClockStation, WriteChannelMode, [newMode, flag])
    } finally {
      setLoading(false);
    }
  }
  const blocksConfig: BlockConfigItem[] = BlocksControlUtils.getBlocksConfig(blocks);

  const TooltipIcon = ({blockText}: { blockText: string }) => (
    <Whisper
      placement="top"
      controlId={`tooltip-${blockText}`}
      speaker={
        <Tooltip>
          {BlocksControlUtils.getTooltipText(blockText)}
        </Tooltip>
      }
    >
      <div style={{cursor: 'pointer'}}>
        <InfoOutlineIcon style={{fontSize: '20px'}} />
      </div>
    </Whisper>
  );

  useEffect(() => {
    getBlocks().then();
    readImpulseForSync().then();
  }, []);

  const sendCommand = async (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    if (!deviceId) return;
    setLoading(true);

    try {
      const result = await SharedApi.updateBlocks(String(deviceId), blocks)
      if (result) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      console.error('Error updating blocks:', error);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  }

  const getBlocks = async () => {
    setLoading(true);

    try {
      const response = await SharedApi.getBlocks(String(deviceId));

      if (response) {
        setBlocks(response);
        setEnabled(true);
      }

    } catch (error) {
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
      console.error('Error fetching blocks:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleToggleChange = (key: string, value: boolean) => {
    if (key.startsWith('channelLocks')) {
      const channelIndex = parseInt(key.replace('channelLocks', ''));
      setBlocks(prev => ({
        ...prev,
        channelLocks: prev.channelLocks.map((lock, index) =>
          index === channelIndex ? value : lock
        )
      }));
    } else {
      setBlocks(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const readImpulseForSync = async () => {
    setLoading(true);
    try {
      const newImpulseData = structuredClone(impulseDataList);
      const resultImpulse = await executeCommand(ClockStation, ReadImpulse, []) as ExecuteCommandResult;
      const arr = NumberToBitArray(resultImpulse.Params[5]);

      resultImpulse.Params.slice(0, 4).map((x, i) => {
        newImpulseData[i].enabled = !!arr[7 - i];
        newImpulseData[i].sync = !!arr[3 - i];
      });

      setMode(resultImpulse.Params[4]);
      setImpulseDataList(newImpulseData);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToggle = (channel: number) => async (value: boolean) => {
    setImpulseDataList(prev => {
      const updated = structuredClone(prev);
      updated[channel].sync = value;
      writeChannelStateCommand(mode, updated).catch(console.error);
      return updated;
    });
  };



  return (
    <>
      <div className="row mb-2">
        <div className="col-xxl-6 col-lg-6 col-md-6 col-sm-12">
          <div className="fieldset-wrapper">
            <div className="legend-title main-legend-title">Блокировки</div>
            <section className="secondary-clock">
              <div className="row p-2 pb-0">
                <div className="col-12 d-flex justify-content-end gap-2">
                  <Whisper placement="top" speaker={<Tooltip>Сохранить</Tooltip>}>
                    <IconButton
                      style={{marginRight: '10px'}}
                      icon={<TfiSaveIcon/>}
                      size="sm"
                      loading={loading}
                      onClick={sendCommand}
                      className="refresh-btn"
                    />
                  </Whisper>
                  <UpdateButton loading={loading} onRefresh={getBlocks}/>
                </div>
              </div>
              <div className="row p-2">
                <fieldset disabled={!enabled}>
                  {blocksConfig.map((block, index) => (
                    <div key={block.key} className="col-md-12 col-sm-12">
                      <div className="row mb-2">
                        <div className="col-md-9 input-group ">
                          <div className="input-group-text col-9">
                            <label htmlFor={`blockEnabled${index}`} className="form-label">
                              {block.text}
                            </label>
                          </div>
                          <div className="col-md-2">
                            <div className="form-control" style={{maxWidth: '70px', textAlign: 'center', borderRadius: 0}}>
                              <Toggle
                                className="mt-1 mb-1"
                                color="blue"
                                checkedChildren="ON"
                                unCheckedChildren="OFF"
                                id={`blockEnabled${index}`}
                                checked={block.value}
                                onChange={(value) => handleToggleChange(block.key, value)}
                              />
                            </div>
                          </div>
                          <div className="col-md-1 justify-content-start align-items-center d-flex">
                            <TooltipIcon blockText={block.text} />
                          </div>
                        </div>
                      </div>
                      {/*<div className="input-group mb-3">*/}
                      {/*  <div className="input-group-text col-9">*/}
                      {/*    <label htmlFor={`blockEnabled${index}`} className="form-label">*/}
                      {/*      {block.text}*/}
                      {/*    </label>*/}
                      {/*  </div>*/}
                      {/*  <div className="form-control col-md-2" style={{maxWidth: '60px', textAlign: 'center'}}>*/}
                      {/*    <Toggle*/}
                      {/*      style={{margin: '5px'}}*/}
                      {/*      id={`blockEnabled${index}`}*/}
                      {/*      color={'green'}*/}
                      {/*      checked={block.value}*/}
                      {/*      onChange={(value) => handleToggleChange(block.key, value)}*/}
                      {/*    />*/}
                      {/*  </div>*/}
                      {/*  <div className="col-md-1 p-3 text-center">*/}
                      {/*    <TooltipIcon blockText={block.text} />*/}
                      {/*  </div>*/}
                      {/*</div>*/}
                    </div>
                  ))}
                </fieldset>
              </div>
            </section>
          </div>
        </div>
        <div className="col-xxl-6 col-lg-6 col-md-6 col-sm-12">
          <div className="fieldset-wrapper mt-4">
            <div className="legend-title main-legend-title">Синхронизация электронных часов</div>
            <section className="secondary-clock">
              <div className="row p-2 pb-0">
                <div className="col-12 d-flex justify-content-end gap-2">
                  <UpdateButton loading={loading} onRefresh={readImpulseForSync} />
                </div>
              </div>

              <div className="row p-2">
                <fieldset className="w-100">
                  <div className="row">
                    {impulseDataList.map((x, index) => (
                      <div key={index} className="col-md-6 col-sm-12">
                        <div className="input-group mb-2">
                          <div className="input-group-text w-50">
                            <label className="form-label mb-0">
                              Канал {x.channel + 1}
                            </label>
                          </div>
                          <div
                            className="form-control d-flex justify-content-center"
                            style={{ maxWidth: '140px', borderRadius: 0 }}
                          >
                            <Toggle
                              className="mt-1 mb-1"
                              color="blue"
                              checkedChildren="ON"
                              unCheckedChildren="OFF"
                              checked={x.sync}
                              loading={loading}
                              onChange={handleSyncToggle(x.channel)}
                            >
                            </Toggle>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  )
}

export default BlocksControl;
